import moment from 'moment';
import { z } from 'zod';

import env from '../../env';
import { createApi } from '../core';

export type StudentDetails = {
  sfid?: string;
  firstName: string;
  lastName: string;
  state: string | null;
  postcode: string | null;
  yearLevel: string;
};

export type SignUpForUnderagedChildParams = {
  leadId: string;
  countryCode: 'AU' | 'NZ';
  mobile: string;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
};

export const clueyPlusApi = createApi.group(['clueyPlus'], () => {
  return {
    signUp: createApi.mutation({
      mutationFn: (params: { leadId: string; countryCode: 'AU' | 'NZ'; students: StudentDetails[] }) => {
        const isNZ = params.countryCode === 'NZ';
        return createApi.fetch({
          basePath: 'mulesoftCustomer',
          path: ['internal', 'events', 'signUp', 'trigger'],
          method: 'POST',
          body: {
            returnResult: false,
            userIdentifierField: {
              type: 'leadId',
              value: params.leadId,
            },
            event: {
              type: 'clueyPlus',
              sourceSystem: 'Hub',
            },
            data: {
              countryCode: params.countryCode,
              customerType: 'New',
              studentDetails: params.students,
              productSubscriptionId: isNZ
                ? env.REACT_APP_CLUEY_PLUS_TRIAL_PRODUCT_SUBSCRIPTION_ID_NZ
                : env.REACT_APP_CLUEY_PLUS_TRIAL_PRODUCT_SUBSCRIPTION_ID_AU,
            },
          },
          validator: z.unknown(),
          sign: true,
        });
      },
    }),
    signUpNewStudent: createApi.mutation({
      mutationFn: (params: { accountId: string; countryCode: 'AU' | 'NZ'; students: StudentDetails[] }) => {
        const isNZ = params.countryCode === 'NZ';
        return createApi.fetch({
          basePath: 'mulesoftCustomer',
          path: ['internal', 'events', 'signUp', 'trigger'],
          method: 'POST',
          body: {
            returnResult: false,
            userIdentifierField: {
              type: 'accountId',
              value: params.accountId,
            },
            event: {
              type: 'clueyPlus',
              sourceSystem: 'Hub',
            },
            data: {
              countryCode: params.countryCode,
              billingAccountId: params.accountId,
              customerType: 'Existing',
              studentDetails: params.students,
              productSubscriptionId: isNZ
                ? env.REACT_APP_CLUEY_PLUS_TRIAL_PRODUCT_SUBSCRIPTION_ID_NZ
                : env.REACT_APP_CLUEY_PLUS_TRIAL_PRODUCT_SUBSCRIPTION_ID_AU,
            },
          },
          validator: z.unknown(),
          sign: true,
        });
      },
    }),
    resubscribeToClueyPlus: createApi.mutation({
      mutationFn: (params: { accountId: string; countryCode: 'AU' | 'NZ'; students: StudentDetails[] }) => {
        const isNZ = params.countryCode === 'NZ';
        return createApi.fetch({
          basePath: 'mulesoftCustomer',
          path: ['internal', 'events', 'signUp', 'trigger'],
          method: 'POST',
          body: {
            returnResult: false,
            userIdentifierField: {
              type: 'accountId',
              value: params.accountId,
            },
            event: {
              type: 'clueyPlus',
              sourceSystem: 'Hub',
            },
            data: {
              countryCode: params.countryCode,
              billingAccountId: params.accountId,
              customerType: 'Existing',
              studentDetails: params.students,
              productSubscriptionId: isNZ
                ? env.REACT_APP_CLUEY_PLUS_PRODUCT_SUBSCRIPTION_ID_NZ
                : env.REACT_APP_CLUEY_PLUS_PRODUCT_SUBSCRIPTION_ID_AU,
            },
          },
          validator: z.unknown(),
          sign: true,
        });
      },
    }),
    signUpForUnderagedChild: createApi.mutation({
      mutationFn: (params: SignUpForUnderagedChildParams) => {
        const { countryCode, leadId, mobile, name, email, firstName, lastName } = params;
        return createApi.fetch({
          basePath: 'studentHubAPI',
          path: ['parent-sign-up'],
          method: 'POST',
          body: {
            dryRun: false,
            parentDetails: {
              name,
              email,
              mobile,
              countryCode,
              firstName,
              lastName,
            },
            studentDetails: {
              id: leadId,
            },
          },
          validator: z.unknown(),
          sign: true,
        });
      },
    }),
    trialConversion: createApi.mutation({
      mutationFn: (params: {
        accountId: string;
        customerSubscriptionId: string;
        countryCode: 'AU' | 'NZ';
        discountId?: string;
        effectiveFromDate?: string;
      }) => {
        const isNZ = params.countryCode === 'NZ';
        return createApi.fetch({
          basePath: 'mulesoftCustomer',
          path: ['internal', 'events', 'trialConversion', 'trigger'],
          method: 'POST',
          body: {
            returnResult: false,
            userIdentifierField: {
              type: 'accountId',
              value: params.accountId,
            },
            event: {
              type: 'cluey-plus-trial-conversion',
              sourceSystem: 'customer-hub',
            },
            data: {
              customerSubscriptionId: params.customerSubscriptionId,
              productSubscriptionId: isNZ
                ? env.REACT_APP_CLUEY_PLUS_PRODUCT_SUBSCRIPTION_ID_NZ
                : env.REACT_APP_CLUEY_PLUS_PRODUCT_SUBSCRIPTION_ID_AU,
              effectiveFromDate: params.effectiveFromDate || moment().utc().format('YYYY-MM-DD'),
              discountId: params.discountId ? params.discountId : undefined,
            },
          },
          validator: z.unknown(),
          sign: true,
        });
      },
    }),
    cancelSubscription: createApi.mutation({
      mutationFn: (params: {
        accountId: string;
        customerSubscriptionId: string;
        enrolmentCancellationDate: string;
        enrolmentId: string;
      }) => {
        return createApi.fetch({
          basePath: 'mulesoftCustomer',
          path: ['internal', 'events', 'cancel', 'trigger'],
          method: 'POST',
          body: {
            returnResult: false,
            userIdentifierField: {
              type: 'accountId',
              value: params.accountId,
            },
            event: {
              type: 'clueyPlus',
              sourceSystem: 'customer-hub',
            },
            data: {
              customerSubscriptionId: params.customerSubscriptionId,
              enrolmentCancellationDate: params.enrolmentCancellationDate,
              enrolmentId: params.enrolmentId,
            },
          },
          validator: z.unknown(),
          sign: true,
        });
      },
    }),
    applyDiscount: createApi.mutation({
      mutationFn: (params: { accountId: string; customerSubscriptionId: string; discountId?: string }) => {
        return createApi.fetch({
          basePath: 'mulesoftCustomer',
          path: ['internal', 'events', 'applyDiscount', 'trigger'],
          method: 'POST',
          body: {
            returnResult: false,
            userIdentifierField: {
              type: 'accountId',
              value: params.accountId,
            },
            event: {
              type: 'clueyPlus',
              sourceSystem: 'customer-hub',
            },
            data: {
              customerSubscriptionId: params.customerSubscriptionId,
              discountId: params.discountId ? params.discountId : undefined,
            },
          },
          validator: z.unknown(),
          sign: true,
        });
      },
    }),
  };
});
